import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { bulkImportQuestions } from '../../services/adminApi';

const BulkImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    // Validate file type
    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (selectedFile.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setFile(selectedFile);
    setResults(null);
    previewFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length > 0) {
        const headers = lines[0].split(',').map(h => h.trim());
        const dataRows = lines.slice(1, 6).map(line => {
          const values = line.split(',').map(v => v.trim());
          return headers.reduce((obj, header, index) => {
            obj[header] = values[index] || '';
            return obj;
          }, {});
        });
        
        setPreviewData({
          headers,
          rows: dataRows,
          totalRows: lines.length - 1
        });
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate progress (since fetch doesn't support upload progress easily)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await bulkImportQuestions(file, skipDuplicates);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      
      // Log the result to debug
      console.log('Import result:', result);
      console.log('Skipped records:', result.skipped_records);
      
      
      console.log('Import result:', result);
      console.log('Failed records:', result.errors);

      
      setResults(result);
      
      // Show summary toast
      const successCount = result.successful || 0;
      const skippedCount = result.skipped || 0;
      const failedCount = result.failed || 0;
      
      if (failedCount === 0) {
        toast.success(`Successfully imported ${successCount} questions! ${skippedCount > 0 ? `(${skippedCount} skipped)` : ''}`);
      } else {
        toast.warning(`Imported ${successCount} questions, ${failedCount} failed, ${skippedCount} skipped`);
      }
      
      // Clear file after 3 seconds if all successful
      if (failedCount === 0) {
        setTimeout(() => {
          setFile(null);
          setPreviewData(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file: ' + error.message);
      setResults(null);
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResults(null);
    setPreviewData(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = [
      ['question_text', 'question_type', 'difficulty', 'topic', 'options', 'correct_answer', 'explanation'],
      ['What is 2 + 2?', 'multiple_choice', 'easy', 'Mathematics', '["2", "3", "4", "5"]', '4', 'Basic addition: 2 + 2 = 4'],
      ['Is Python a programming language?', 'true_false', 'easy', 'Programming', '["True", "False"]', 'True', 'Python is indeed a programming language'],
      ['What is the capital of France?', 'multiple_choice', 'medium', 'Geography', '["London", "Berlin", "Paris", "Madrid"]', 'Paris', 'Paris is the capital and largest city of France'],
      ['The Earth revolves around the Sun', 'true_false', 'easy', 'Science', '["True", "False"]', 'True', 'The Earth orbits around the Sun, taking approximately 365 days to complete one revolution']
    ];

    const csvContent = sampleData.map(row => 
      row.map(cell => {
        // Escape quotes and wrap in quotes if contains comma or quote
        if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
          return `"${cell.replace(/"/g, '""')}"`;
        }
        return cell;
      }).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_questions_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Sample CSV template downloaded');
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-file-upload mr-2"></i>
          Bulk Import Questions
        </h1>
        <div>
          <Link
            to="/admin"
            className="btn btn-sm btn-secondary mr-2"
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back to Dashboard
          </Link>
          <button
            className="btn btn-sm btn-info mr-2"
            onClick={downloadSampleCSV}
          >
            <i className="fas fa-download mr-1"></i>
            Download Sample CSV
          </button>
          <Link
            to="/admin/questions"
            className="btn btn-sm btn-primary"
          >
            <i className="fas fa-list mr-1"></i>
            View All Questions
          </Link>
        </div>
      </div>

      {/* Instructions Card */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-info-circle mr-2"></i>
            Instructions
          </h6>
        </div>
        <div className="card-body">
          <div className="alert alert-info mb-0">
            <h6 className="font-weight-bold">CSV File Format Requirements:</h6>
            <ul className="mb-2">
              <li><strong>Required Headers:</strong> question_text, question_type, difficulty, topic, options, correct_answer, explanation</li>
              <li><strong>Question Types:</strong> multiple_choice, true_false</li>
              <li><strong>Difficulty Levels:</strong> easy, medium, hard</li>
              <li><strong>Options Format:</strong> JSON array string (e.g., ["Option 1", "Option 2", "Option 3", "Option 4"])</li>
              <li><strong>For Multiple Choice:</strong> Provide 2-6 options in the options field</li>
              <li><strong>For True/False:</strong> Use options ["True", "False"]</li>
              <li><strong>Maximum File Size:</strong> 10MB</li>
              <li><strong>Encoding:</strong> UTF-8</li>
            </ul>
            <p className="mb-0">
              <i className="fas fa-lightbulb text-warning mr-1"></i>
              <strong>Tip:</strong> Download the sample CSV template to see the correct format.
            </p>
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">
            <i className="fas fa-upload mr-2"></i>
            Upload CSV File
          </h6>
        </div>
        <div className="card-body">
          {/* Dropzone */}
          <div
            className={`border-dashed p-5 text-center ${dragActive ? 'bg-light border-primary' : 'border-secondary'}`}
            style={{
              border: '2px dashed',
              borderRadius: '8px',
              cursor: 'pointer'
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fas fa-cloud-upload-alt fa-3x text-primary mb-3"></i>
            <h5>Drag and drop your CSV file here</h5>
            <p className="text-muted">or click to browse</p>
            {file && (
              <div className="mt-3">
                <span className="badge badge-success p-2">
                  <i className="fas fa-file-csv mr-2"></i>
                  {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </span>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {/* Options */}
          <div className="mt-4">
            <div className="custom-control custom-checkbox">
              <input
                type="checkbox"
                className="custom-control-input"
                id="skipDuplicates"
                checked={skipDuplicates}
                onChange={(e) => setSkipDuplicates(e.target.checked)}
              />
              <label className="custom-control-label" htmlFor="skipDuplicates">
                Skip duplicate questions (based on question text)
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-4">
            <button
              className="btn btn-primary mr-2"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Uploading...
                </>
              ) : (
                <>
                  <i className="fas fa-upload mr-1"></i>
                  Upload and Import
                </>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={uploading}
            >
              <i className="fas fa-times mr-1"></i>
              Clear
            </button>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-4">
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${uploadProgress}%` }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preview Section */}
      {previewData && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-eye mr-2"></i>
              Preview (First 5 rows of {previewData.totalRows} total)
            </h6>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead>
                  <tr>
                    {previewData.headers.map((header, index) => (
                      <th key={index} className="text-nowrap">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {previewData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="text-nowrap" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="card shadow mb-4">
          <div className="card-header py-3">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-check-circle mr-2"></i>
              Import Results
            </h6>
          </div>
          <div className="card-body">
            {/* Summary Cards */}
            <div className="row mb-4">
              <div className="col-md-3">
                <div className="card border-left-primary">
                  <div className="card-body py-3">
                    <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                      Total Processed
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {results.total_processed || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-left-success">
                  <div className="card-body py-3">
                    <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                      Successful
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {results.successful || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-left-warning">
                  <div className="card-body py-3">
                    <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                      Skipped
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {results.skipped || 0}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-left-danger">
                  <div className="card-body py-3">
                    <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                      Failed
                    </div>
                    <div className="h5 mb-0 font-weight-bold text-gray-800">
                      {results.failed || 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Skipped Records */}
            {results.skipped_records && results.skipped_records.length > 0 && (
              <div className="mb-4">
                <h6 className="font-weight-bold text-warning">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  Skipped Records ({results.skipped_records.length})
                </h6>
                <div className="table-responsive" style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th>Row</th>
                        <th>Question Text</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.skipped_records.map((record, index) => (
                        <tr key={index}>
                          <td>{record.row_number || index + 1}</td>
                          <td>{record.question_text || 'N/A'}</td>
                          <td className="text-muted">{record.reason || 'Duplicate'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Failed Records */}
            {results.errors && results.errors.length > 0 && (
              <div className="mb-4">
                <h6 className="font-weight-bold text-danger">
                  <i className="fas fa-times-circle mr-2"></i>
                  Failed Records ({results.errors.length})
                </h6>
                <div className="table-responsive" style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <table className="table table-sm table-bordered">
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '80px' }}>Row</th>
                        <th style={{ minWidth: '200px' }}>Field</th>
                        <th style={{ minWidth: '250px' }}>Error Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.errors.map((record, index) => {
                        // Handle different possible field names from backend
                        const rowNumber = record.row_number || record.row || record.line || index + 2; // +2 because row 1 is header
                        const field = record.question_text || record.question || record.data?.question_text || record.field || 'N/A';
                        const errorMessage = record.error || record.error_message || record.reason || record.details || 'Unknown error';
                        
                        return (
                          <tr key={index}>
                            <td>{rowNumber}</td>
                            <td style={{ wordBreak: 'break-word' }}>
                              {field.length > 100 ? field.substring(0, 100) + '...' : field}
                            </td>
                            <td className="text-danger" style={{ wordBreak: 'break-word' }}>
                              <small>{errorMessage}</small>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Success Message */}
            {results.failed === 0 && results.successful > 0 && (
              <div className="alert alert-success mb-0">
                <i className="fas fa-check-circle mr-2"></i>
                All questions imported successfully! You can now view them in the questions list.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkImport;
